import { Checkbox, Stack, TableCell, Tooltip, Typography } from '@mui/material';

import { type AccessRegistryEntities, cellKey } from '@/Contexts/PermissionMatrix/Context';

interface KeyCollection {
  has(key: string): boolean; // for Sets and Maps
}

interface ActionHeaderCellProps {
  action: AccessRegistryEntities[number]['actions'][number];
  entities: AccessRegistryEntities;
  selectedKeys: KeyCollection;
  readOnlySelectedKeys?: KeyCollection; // basically grants gained throuh roles that cannot be changed (only used on Permission Matrix)
  onChange: (action: string, entityKeys: string[], checked: boolean) => void;
}

const ActionHeaderCell = ({
  action,
  entities,
  selectedKeys,
  readOnlySelectedKeys,
  onChange,
}: ActionHeaderCellProps) => {
  const supportedEntities = entities.filter((entity) =>
    entity.actions.some((entityAction) => entityAction.key === action.key),
  );
  const editableEntities = supportedEntities.filter(
    (entity) => !readOnlySelectedKeys?.has(cellKey(action.key, entity.key)),
  ); // for handling disabling in case a permission is granted via a Role
  const isSelected = (entity: AccessRegistryEntities[number]) => {
    const key = cellKey(action.key, entity.key);
    return selectedKeys.has(key) || readOnlySelectedKeys?.has(key) === true;
  };
  const allSelected = supportedEntities.length > 0 && supportedEntities.every(isSelected);
  const someSelected = supportedEntities.some(isSelected);

  return (
    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f7f7f7', whiteSpace: 'nowrap' }}>
      <Stack direction="row" alignItems="center" justifyContent="center">
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          disabled={editableEntities.length === 0}
          onChange={() =>
            onChange(
              action.key,
              editableEntities.map((entity) => entity.key),
              !allSelected,
            )
          }
        />
        <Tooltip title={action.description ?? ''} arrow disableHoverListener={!action.description}>
          <Typography fontWeight={800} variant="body2" sx={{ cursor: action.description ? 'help' : 'default' }}>
            {action.name}
          </Typography>
        </Tooltip>
      </Stack>
    </TableCell>
  );
};

export default ActionHeaderCell;
